import { showError, showHttpError, showSuccess } from '@calendly/alert'
import { FormError } from '@calendly/form'
import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk } from '@reduxjs/toolkit'

import { setFormErrors } from 'actions/form'
import { setOrganization } from 'common'

import { isNewSelector } from './selectors'

export const fetchAggregateSsoSettings = createAsyncThunk(
  'organizationSettings/sso/fetchAggregateSsoSettings',
  async (_, { dispatch, rejectWithValue }) => {
    const [samlSuccess, samlResponse] = await httpRequest({
      url: '/api/organization/identity_provider',
      method: 'GET',
    })

    if (!samlSuccess) {
      dispatch(showHttpError(samlResponse))
      return rejectWithValue({})
    }

    return samlResponse || {}
  })

export const saveSamlSsoSettings = createAsyncThunk(
  'organizationSettings/sso/saveSamlSsoSettings',
  async (settings, { dispatch, getState, rejectWithValue }) => {
    const isNew = isNewSelector(getState())
    const [success, response] = await httpRequest({
      url: '/api/organization/identity_provider',
      method: isNew ? 'POST' : 'PUT',
      data: {
        identity_provider: {
          entity_id: settings.entity_id,
          sso_url: settings.sso_url,
          certificate: settings.certificate
        },
        session_settings: settings.session_settings
      }
    })

    if (!success) {
      let errorPayload = response
      try {
        dispatch(setFormErrors(response, { body: 'certificate' }))
      } catch (error) {
        if (error instanceof FormError) {
          errorPayload = error.payload
        }
      }
      return rejectWithValue(errorPayload)
    }

    dispatch(setOrganization({ session_timeout_in: response.session_timeout_in }))
    dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
    return response
  })

export const destroySamlSsoSettings = createAsyncThunk(
  'organizationSettings/sso/destroySamlSsoSettings',
  async (_, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/organization/identity_provider',
      method: 'DELETE'
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue({})
    }

    return {}
  })

export const saveTestSamlSettings = createAsyncThunk(
  'organizationSettings/sso/saveTestSamlSettings',
  async ({ settings, nextStep }, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/organization/identity_provider/create_test_idp',
      method: 'POST',
      data: {
        identity_provider: {
          entity_id: settings.entity_id,
          sso_url: settings.sso_url,
          certificate: settings.certificate
        }
      }
    })

    if (!success) {
      let errorPayload = response
      if (response.type === 'validation') {
        try {
          dispatch(setFormErrors(response, { body: 'certificate' }))
        } catch (error) {
          if (error instanceof FormError) {
            errorPayload = error.payload
          }
        }
      }
      // Show server error message for all errors
      dispatch(showError(I18n.t('errors.server.server')))
      return rejectWithValue(errorPayload)
    }

    return {
      settings: response,
      step: nextStep,
      sessionSettings: settings.session_settings.session_timeout_in
    }
  })

export const applyTestSamlSettings = createAsyncThunk(
  'organizationSettings/sso/applyTestSamlSettings',
  async (settings, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/organization/identity_provider/apply_test_idp',
      method: 'PUT',
      data: {
        certificate_expiration: settings.testSamlSettings.certificate_expiration,
        certificate_id: settings.testSamlSettings.certificate_id,
        test_idp_id: settings.testSamlSettings.test_idp_id,
        session_settings: { session_timeout_in: settings.sessionSettings }
      }
    })

    if (!success) {
      // Show generic server error message
      dispatch(showError(I18n.t('errors.server.server')))
      return rejectWithValue(response)
    }

    dispatch(setOrganization({ session_timeout_in: response.session_timeout_in }))
    dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
    return response
  })
